// TileOwnerModal.js
import React, { useEffect, useRef, useContext } from 'react';
import { BackendContext } from '../../Context/BackendContext';
import { useNavigate } from 'react-router-dom';
import { Heart, X, MapPin, Share, SquarePen, Twitter, Instagram, Youtube, Twitch } from 'lucide-react';
import { 
  Divider, 
  Spinner, 
  Modal, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  useDisclosure, 
  Slider, 
  Button, 
  Input, 
  Textarea,
  Tooltip
} from "@heroui/react";
import { nav } from 'framer-motion/client';

// ---- Chunk + RLE helpers ----
const CANVAS_SIZE = 512;

// Draw decoded chunks (white squares)
function drawDecodedMask(ctx, decodedChunks, minCol, minRow, dx, dy, scale) {
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = 'white';
  for (const ch of decodedChunks) {
    const { origin, width, height, bits } = ch;
    const baseX = origin.col1 - 1; // 0-based world
    const baseY = origin.row1 - 1;
    for (let j = 0; j < height; j++) {
      const rowOff = j * width;
      for (let i = 0; i < width; i++) {
        if (!bits[rowOff + i]) continue;
        const worldX = baseX + i;
        const worldY = baseY + j;
        const localX = worldX - (minCol - 1);
        const localY = worldY - (minRow - 1);
        ctx.fillRect(dx + localX * scale, dy + localY * scale, scale, scale);
      }
    }
  }
}



const ArtworkOwnerModal = ({
  artworkId,
  artwork,
  onCloseModal,
  onRequestSeed,   // (artworkId) => Promise<{ bbox, seedImage }>
  onApplyPatch,    // ({ base64Image, bbox }) => void
}) => {
  const navigate = useNavigate();
  const { canvas, userProfile, extendArtworkExpiresAt, refreshArtworkExpiresAt } = useContext(BackendContext);
  
  const modalRef = useRef(null);
  const canvasRef = useRef(null);

  const [isCallingBackend, setIsCallingBackend] = React.useState(false);

  const [timeRemaining, setTimeRemaining] = React.useState('00:00:00');
  const [hoursLeftForSlider, setHoursLeftForSlider] = React.useState(0)

  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [isEditingDescription, setIsEditingDescription] = React.useState(false);
  const [newTitle, setNewTitle] = React.useState('');
  const [newDescription, setNewDescription] = React.useState('');

  const [buyError, setBuyError] = React.useState('');

  const [extendHours, setExtendHours] = React.useState(1); // Default to 1 day
  
  const [showScrollbar, setShowScrollbar] = React.useState(false);
  const scrollTimeout = useRef(null);

  const [loading, setLoading] = React.useState(false);

  const [isSharing, setIsSharing] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  
  const { isOpen, onOpen, onClose } = useDisclosure();

  // cache of decoded chunks (for fast redraw) + bbox (1-based columns/rows)
  const decodedRef = React.useRef([]); 
  const bboxCacheRef = React.useRef(null);

  const stripDataUrl = (s) => (typeof s === 'string' && s.startsWith('data:')) ? s.split(',')[1] : s;
  const to1Based = (n) => (n == null ? 1 : (Number(n) + 1));

  // Compute bbox from decodedRef (1-based col/row space)
  function computeBBoxFromDecoded(decoded) {
    let minCol =  Infinity, minRow =  Infinity;
    let maxCol = -Infinity, maxRow = -Infinity;

    for (const ch of decoded) {
      const { origin, width, height, bits } = ch;
      const baseX = origin.col1 - 1; // world 0-based
      const baseY = origin.row1 - 1;
      for (let j = 0; j < height; j++) {
        const rowOff = j * width;
        for (let i = 0; i < width; i++) {
          if (!bits[rowOff + i]) continue;
          const x = baseX + i, y = baseY + j;
          if (x + 1 < minCol) minCol = x + 1;
          if (y + 1 < minRow) minRow = y + 1;
          if (x + 1 > maxCol) maxCol = x + 1;
          if (y + 1 > maxRow) maxRow = y + 1;
        }
      }
    }

    if (!Number.isFinite(minCol)) return { minCol:1, minRow:1, maxCol:1, maxRow:1, bboxW:1, bboxH:1 };
    const bboxW = Math.max(1, maxCol - minCol + 1);
    const bboxH = Math.max(1, maxRow - minRow + 1);
    return { minCol, minRow, maxCol, maxRow, bboxW, bboxH };
  }

  // --- Share helpers ---
  const getArtworkShareUrl = React.useCallback(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const pg = artwork?.page ?? 'A';
    return `${origin}/Canvas/${encodeURIComponent(pg)}/${encodeURIComponent(artworkId)}`;
  }, [artwork?.page, artworkId]);

  const copyToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      // Fallback (rare): temporarily select/execCommand
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleShare = async () => {
    if (!artworkId) return;
    const url = getArtworkShareUrl();
    const title = artwork?.title || 'ArtParcel Artwork';
    const text = `Check out this artwork on ArtParcel${artwork?.owner ? ` by @${artwork.owner}` : ''}!`;

    setIsSharing(true);
    try {
      if (navigator?.share && false) {
        // Native share (best UX on mobile)
        await navigator.share({ title, text, url });
      } else {
        // Clipboard fallback
        const ok = await copyToClipboard(url);
        if (ok) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } else {
          // As a last resort, open a Twitter intent prefilled
          const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${text} ${url}`)}`;
          window.open(tw, '_blank', 'noopener,noreferrer');
        }
      }
    } catch (e) {
      // user canceled share or error — no-op
      // console.error('share failed', e);
    } finally {
      setIsSharing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchAndRenderArtwork = async () => {
      setLoading(true);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');

      // Ensure a true 512x512 backing store (not just CSS)
      canvas.width = CANVAS_SIZE;
      canvas.height = CANVAS_SIZE;

      // Background (white); change if you prefer transparent
      ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

      // Pixel-art friendly
      ctx.imageSmoothingEnabled = false;
      ctx.imageSmoothingQuality = 'low';

      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        const srcW = img.width;
        const srcH = img.height;

        if (!srcW || !srcH) {
          setLoading(false);
          return;
        }

        // Compute scale
        const containScale = Math.min(CANVAS_SIZE / srcW, CANVAS_SIZE / srcH);
        const coverScale = Math.max(CANVAS_SIZE / srcW, CANVAS_SIZE / srcH);

        // For pixel art: integer upscale when image is smaller than canvas.
        const chooseScale = (rawScale) => {
          const isUpscale = srcW <= CANVAS_SIZE && srcH <= CANVAS_SIZE;
          if (isUpscale) {
            const intScale = Math.max(1, Math.floor(rawScale));
            return intScale;
          }
          // downscaling or mixed: use the raw (can be fractional)
          return rawScale;
        };
      
        const scale = chooseScale(containScale);
        const destW = Math.round(srcW * scale);
        const destH = Math.round(srcH * scale);
        const dx = Math.floor((CANVAS_SIZE - destW) / 2);
        const dy = Math.floor((CANVAS_SIZE - destH) / 2);

        ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
        ctx.drawImage(img, 0, 0, srcW, srcH, dx, dy, destW, destH);

        setLoading(false);
      };

      img.onerror = () => {
        if (!cancelled) {
          console.error('Failed to load artwork image.');
          setLoading(false);
        }
      };

      img.src = `data:image/png;base64,${artwork.imageData}`;
    };

    fetchAndRenderArtwork();
    return () => { cancelled = true; };
  }, [artwork]);

  // --- Countdown timer for ownership expiration ---
  useEffect(() => {
    const updateCountdown = () => {
      if (!artwork || !artwork.expiresAt) return;

      const expiresAt = new Date(artwork.expiresAt);
      const now = new Date();
      const diff = Math.max(0, expiresAt.getTime() - now.getTime());

      const hours = Math.floor(diff / 1000 / 3600);
      const minutes = Math.floor((diff / 1000 / 60) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setHoursLeftForSlider(hours);
      setTimeRemaining(
        `${String(hours).padStart(2,'0')}:${String(minutes).padStart(2,'0')}:${String(seconds).padStart(2,'0')}`
      );
    };

    if (artwork?.expiresAt) {
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [artwork]);

  // Scrollbar hint hide
  const handleScroll = () => {
    setShowScrollbar(true);
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => setShowScrollbar(false), 1000);
  };

  // --- Actions ---
  const onExtendOwnership = async () => {
    const pricePerHour = canvas?.price || 0;
    const totalCost = pricePerHour * extendHours;

    if ((userProfile?.tokens ?? 0) < totalCost) {
      setBuyError(`Insufficient Pixel Drops to buy ${extendHours} hour(s).`);
      return;
    }

    try {
      setIsCallingBackend(true);
      await extendArtworkExpiresAt(artworkId, extendHours);
      setIsCallingBackend(false);
      setBuyError('');
      onClose(); // close hours modal

    } catch (error) {
      console.error("Failed to extend ownership:", error);
      setBuyError('Failed to extend ownership. Please try again.');
      setIsCallingBackend(false);
    }
  }

  const onRefreshOwnership = async () => {
    try {
      setIsCallingBackend(true);
      await refreshArtworkExpiresAt(artworkId);
      setIsCallingBackend(false);
      setBuyError('');
      onClose(); // close hours modal

    } catch (error) {
      console.error("Failed to extend ownership:", error);
      setBuyError('Failed to extend ownership. Please try again.');
      setIsCallingBackend(false);
    }
  }

  const onGoToPage = async () => {
    navigate(`/Canvas/${artwork?.page ?? ''}`);
  };

  return (
    <>
      {isCallingBackend && (
        <div className="fixed inset-0 z-[1110] bg-black bg-opacity-70 flex items-center justify-center">
          <div className="bg-white rounded-lg p-4 flex items-center gap-2">
            <Spinner color="primary" size="lg" />
            <span className="text-black">Working…</span>
          </div>
        </div>
      )}

      <div className="fixed inset-0 bg-black/60 z-[900] flex items-center justify-center">
        <div
          ref={modalRef}
          className={`pointer-events-auto bg-slate-900/80 rounded-xl p-4 relative max-h-[90vh] w-[550px]`}
        >
          <button
            onClick={onCloseModal}
            className="absolute top-4 right-4 text-zinc-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>

          <h2 className="mb-2 text-center font-bold">
            {artwork?.title ?? '—'}
          </h2>
          
          {/* Scrollable content container */}
          <div
            className="overflow-y-visible overflow-x-hidden max-h-[calc(85vh-100px)] w-full"
            onScroll={handleScroll}
            style={{ scrollbarWidth: showScrollbar ? 'none' : 'none' }}
          >
            {/* Artwork-only canvas */}
            <div className="relative w-full h-[512px] flex items-center justify-center">
              <canvas
                ref={canvasRef}
                style={{ imageRendering: 'pixelated', border: '1px solid black', cursor: 'crosshair' }}
              />
            </div>

            {/* Likes + tag */}
            <div className="mt-2 mb-2 flex justify-between items-center gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Button isIconOnly aria-label="Like" color="default">
                  <Heart />
                </Button>
                <p className="text-l font-bold text-zinc-200">{artwork?.likesCount ?? 0}</p>
              </div>
              <Tooltip
                content="Copied!"
                color="success"
                placement="top"
                isOpen={copied}          // shows only when copied === true
                motionProps={{
                  variants: {
                    enter: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
                    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } },
                  },
                }}
              >
                <Button
                  isIconOnly
                  size="md"
                  aria-label="Share"
                  onPress={handleShare}
                  isLoading={isSharing}
                  className={`bg-zinc-700 text-xs px-2 py-1 ${copied ? 'bg-green-700' : ''}`}
                  title="Share Artwork"
                >
                  <Share className="text-gray-300" />
                </Button>
              </Tooltip>
            </div>

            {artwork && (
              <>
                {/* Header row: Title + location */}
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    {isEditingTitle ? (
                      <div className="flex flex-col w-full gap-1">
                        <Input
                          type="text"
                          variant="bordered"
                          defaultValue={artwork?.title ?? ''}
                          maxLength={42}
                          className="px-2 py-1 rounded-md text-white"
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                        />
                      </div>
                    ) : (
                      <>
                        <h2 className="text-xl font-bold">{artwork?.title ?? 'Untitled'}</h2>
                        <button
                          className="text-xs text-blue-400 hover:underline"
                          onClick={() => {
                            setIsEditingTitle(true);
                            setNewTitle(artwork?.title || '');
                          }}
                        >
                          <SquarePen className="inline w-4 h-4 mr-1" />
                        </button>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-zinc-400">
                    <MapPin />
                    {/* If you store page/row/column where the artwork originated or its "anchor" */}
                    <p className="text-m font-bold">
                      {artwork?.page ? `${artwork.page}` : '—'}
                    </p>
                  </div>
                </div>

                {/* Owner */}
                <div className="mb-4">
                  <div className="text-zinc-400 text-xs mb-1">OWNER</div>
                  <div className="flex justify-between items-center">
                    <div
                      className="text-blue-400 font-medium cursor-pointer hover:underline"
                      onClick={() => navigate(`/User/${artwork?.owner ?? ''}`)}
                    >
                      {"@" + (artwork?.owner ?? "N/A")}
                    </div>

                    {/* Socials (optional; requires your data shape) */}
                    {userProfile?.socials && (
                      <div className="flex gap-2 text-zinc-300">
                        {userProfile?.socials.twitter && (
                          <a href={`https://twitter.com/${userProfile?.socials.twitter}`} target="_blank" rel="noopener noreferrer">
                            <Twitter className="w-4 h-4 hover:text-blue-400" />
                          </a>
                        )}
                        {userProfile?.socials.instagram && (
                          <a href={`https://instagram.com/${userProfile?.socials.instagram}`} target="_blank" rel="noopener noreferrer">
                            <Instagram className="w-4 h-4 hover:text-pink-400" />
                          </a>
                        )}
                        {userProfile?.socials.youtube && (
                          <a href={`https://youtube.com/${userProfile?.socials.youtube}`} target="_blank" rel="noopener noreferrer">
                            <Youtube className="w-4 h-4 hover:text-red-700" />
                          </a>
                        )}
                        {userProfile?.socials.twitch && (
                          <a href={`https://twitch.tv/${userProfile?.socials.twitch}`} target="_blank" rel="noopener noreferrer">
                            <Twitch className="w-4 h-4 hover:text-purple-500" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Info Section */}
                <div className="grid grid-cols-3 gap-4 mb-4 text-sm">
                  <div>
                    <div className="text-zinc-400 text-xs">EXPIRATION</div>
                    <div className={timeRemaining === '00:00:00' ? "text-white-400" : "font-semibold text-purple-400"}>
                      {timeRemaining}
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-400 text-xs">SIZE</div>
                    <div className="font-semibold">
                      {artwork?.size ? `${artwork.size} px` : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-400 text-xs">CREATE DATE</div>
                    <div className="font-semibold">
                      {artwork?.createdAt
                        ? new Date(artwork.createdAt).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                            timeZone: 'America/Phoenix', // optional, pick your TZ
                          })
                        : '—'}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-4 mt-4">
                  <div className="text-zinc-400 text-xs mb-1 flex justify-between items-center">
                    <span>DESCRIPTION</span>
                    {!isEditingDescription && (
                      <button
                        className="text-xs text-blue-400 hover:underline"
                        onClick={() => {
                          setIsEditingDescription(true);
                          setNewDescription(artwork?.description || '');
                        }}
                      >
                        <SquarePen className="inline w-4 h-4 mr-1" />
                      </button>
                    )}
                  </div>

                  {isEditingDescription ? (
                    <div className="flex flex-col gap-1">
                      <Textarea
                        className="px-2 py-1 rounded-md text-white"
                        rows={3}
                        defaultValue={artwork?.description ?? ''}
                        maxLength={225}
                        value={newDescription}
                        onChange={(e) => setNewDescription(e.target.value)}
                      />
                    </div>
                  ) : (
                    <div className="font-semibold">
                      {artwork?.description ? artwork.description : "N/A"}
                    </div>
                  )}
                </div>
            <Button
              onPress={onOpen}
              className="w-full px-26 py-2 border-2 border-zinc-200 text-white rounded-2xl transition-colors bg-slate-800 hover:bg-indigo-700"
            >
              Delete Artwork
            </Button>


              </>
            )}
          </div>

          {/* Separator line */}
          <Divider className="my-4" />

          {/* Footer actions */}
          <div className="flex mt-4 gap-2">
            <Button
              onPress={onOpen}
              className="w-1/2 px-26 py-2 border-2 border-zinc-200 text-white rounded-2xl transition-colors bg-slate-800 hover:bg-indigo-700"
            >
              Refresh Ownership
            </Button>
            <Button
              onPress={onGoToPage}
              className="w-1/2 px-26 py-2 border-2 border-zinc-200 text-white rounded-2xl transition-colors bg-slate-800 hover:bg-indigo-700"
            >
              Go To Page
            </Button>
          </div>
        </div>
      </div>
    
      {/* Refresh ownership modal */}
      {artwork && (
        <Modal isOpen={isOpen} onClose={onClose} backdrop="blur" classNames={{ wrapper: "z-[1999]" }}>
          <ModalContent>
            <ModalHeader className="text-lg font-bold">Refresh Artwork Ownership</ModalHeader>
            <ModalBody>
              <p>{`Are you sure you want to refresh the artwork expiration time to ${userProfile.extendTime ?? 12} hours?`}</p>
              {buyError && <p className="text-red-400 text-sm mt-1">{buyError}</p>}
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="light" onPress={onClose}>
                Cancel
              </Button>
              <Button color="primary" 
                onPress={onRefreshOwnership}
                isLoading={isCallingBackend}
              >
                {`Confirm`}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ArtworkOwnerModal;
